import { useEffect, useState } from 'preact/hooks'

import { api } from '../api.js'
import { t } from '../i18n/t.js'
import { onEvent } from '../ws.js'

/**
 * Der permanente Standort-Banner (ARCHITECTURE.md §8, ADR-009 Bedingung 3).
 *
 * **Diese Komponente ist nicht wegklickbar, und das ist keine Nachlässigkeit.**
 * Sie ist die Zusage, die die App von Stalkerware unterscheidet: Es gibt keinen
 * Zustand, in dem jemand den Standort einer Person sieht, ohne dass diese es
 * auf jedem Bildschirm angezeigt bekommt.
 *
 * Deshalb hängt sie in der App-Hülle und nicht im Standort-Bereich. Wer sie
 * dorthin verschiebt, macht sie unsichtbar, sobald jemand im Chat ist — und
 * damit wertlos.
 *
 * Wer hier jemals ein „Schließen"-Kreuz ergänzt, hebt ADR-009 auf. Bitte
 * vorher den Threat-Model-Eintrag T14 lesen.
 */

interface Stand {
  aktive: Array<{
    id: string; viewerId: string; origin: string; precision: string
    expiresAt: string | null; stoppbar: boolean
  }>
  guardianAktiv: boolean
}

export function StandortBanner() {
  const [stand, setStand] = useState<Stand | null>(null)
  const [namen, setNamen] = useState<Record<string, string>>({})
  const [jetzt, setJetzt] = useState(Date.now())

  async function laden() {
    try {
      setStand(await api.get<Stand>('/location/me'))
    } catch {
      // Bewusst still: Ein Fehler beim Laden darf den Banner nicht dazu
      // bringen zu verschwinden. Er bleibt beim letzten bekannten Stand.
    }
  }

  useEffect(() => {
    void laden()
    void api.get<Array<{ id: string; displayName: string }>>('/family/members')
      .then((m) => setNamen(Object.fromEntries(m.map((p) => [p.id, p.displayName]))))
      .catch(() => undefined)

    // Die Restzeit läuft sichtbar herunter. Ein Banner, der „noch 43 Min."
    // sagt und das eine Stunde lang behauptet, ist irreführend.
    const uhr = setInterval(() => setJetzt(Date.now()), 30_000)
    return () => clearInterval(uhr)
  }, [])

  useEffect(() => onEvent((e) => {
    if (e.type.startsWith('location.share')) void laden()
  }), [])

  if (!stand || stand.aktive.length === 0) return null

  const namenListe = stand.aktive
    .map((s) => namen[s.viewerId] ?? t('jemandem'))
    .join(', ')

  // Die kürzeste Restzeit bestimmt, was angezeigt wird — sie läuft zuerst ab.
  const naechsterAblauf = stand.aktive
    .map((s) => (s.expiresAt ? new Date(s.expiresAt).getTime() : null))
    .filter((z): z is number => z !== null)
    .sort((a, b) => a - b)[0]

  const restMinuten = naechsterAblauf
    ? Math.max(0, Math.round((naechsterAblauf - jetzt) / 60_000))
    : null

  const alleDauerhaft = stand.aktive.every((s) => s.expiresAt === null)
  const alleStoppbar = stand.aktive.every((s) => s.stoppbar)

  return (
    <div
      class={`fh-banner fh-banner--location${stand.guardianAktiv ? ' fh-banner--guardian' : ''}`}
      role="status"
    >
      <span aria-hidden="true">📍</span>

      <div class="fh-banner__text">
        <strong>
          {t('Dein Standort wird geteilt mit {namen}', { namen: namenListe })}
          {restMinuten !== null && ` — ${t('noch {n} Min.', { n: restMinuten })}`}
          {restMinuten === null && alleDauerhaft && ` — ${t('bis du es stoppst')}`}
        </strong>

        {stand.guardianAktiv && (
          <p class="fh-muted">
            {/*
              ADR-009, Bedingung 3 und 5: Das Kind erfährt, WARUM es nicht
              stoppen kann, und dass es einen Weg gibt, gehört zu werden.
            */}
            {t('Das haben deine Eltern eingestellt. Du kannst es nicht selbst ausschalten — aber du kannst sagen, dass du das nicht möchtest.')}
            {' '}<a href="#standort">{t('Standort öffnen')}</a>
          </p>
        )}
      </div>

      {alleStoppbar ? (
        <button
          class="fh-btn fh-btn--gefahr fh-banner__action"
          onClick={() => { void api.post('/location/stop', {}).then(() => laden()) }}
        >
          {t('Teilen beenden')}
        </button>
      ) : (
        // Kein Stopp-Knopf, aber auch keine Sackgasse: Der Weg zum
        // Widerspruch steht dort, wo der Stopp stünde.
        <a class="fh-btn fh-btn--quiet fh-banner__action" href="#standort">
          {t('Was heißt das?')}
        </a>
      )}
    </div>
  )
}
